// Material Kit 2 React Base Styles
import colors from "assets/theme/base/colors";

// Material Kit 2 React Helper Functions
import pxToRem from "assets/theme/functions/pxToRem";
import breakpoints from "assets/theme/base/breakpoints";

const { dark } = colors;

const baseProperties = {
    fontFamily: '"Figtree", system-ui, -apple-system, "Segoe UI", Arial, sans-serif',
    fontFamily2: '"Inter", system-ui, -apple-system, "Segoe UI", Arial, sans-serif',
    fontWeightLighter: 100,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontSizeXXS: pxToRem(12), // Reduced for mobile responsiveness
    fontSizeXS: pxToRem(14),
    fontSizeSM: pxToRem(16),
    fontSizeMD: pxToRem(18),
    fontSizeLG: pxToRem(20),
    fontSizeXL: pxToRem(22),
    fontSize2XL: pxToRem(24),
    fontSize3XL: pxToRem(28),
};

const baseHeadingProperties = {
    color: dark.main,
};

const baseDisplayProperties = {
    fontFamily: baseProperties.fontFamily,
    color: dark.main,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.2,
};

const typography = {
    fontFamily: baseProperties.fontFamily,
    fontWeightLighter: baseProperties.fontWeightLighter,
    fontWeightLight: baseProperties.fontWeightLight,
    fontWeightRegular: baseProperties.fontWeightRegular,
    fontWeightMedium: baseProperties.fontWeightMedium,
    fontWeightBold: baseProperties.fontWeightBold,

    h1: {
        fontFamily: baseProperties.fontFamily,
        fontSize: pxToRem(36),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(52),
        },
        lineHeight: 1.25,
        fontWeight: baseProperties.fontWeightBold,
        ...baseHeadingProperties,
    },

    h2: {
        fontFamily: baseProperties.fontFamily,
        fontSize: pxToRem(30),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(44),
        },
        lineHeight: 1.3,
        fontWeight: baseProperties.fontWeightMedium,
        ...baseHeadingProperties,
    },

    subtitle1: {
        fontFamily: baseProperties.fontFamily2,
        fontSize: pxToRem(20),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(22),
        },
        fontWeight: baseProperties.fontWeightMedium,
        lineHeight: 1.625,
    },

    body1: {
        fontFamily: baseProperties.fontFamily2,
        fontSize: pxToRem(18),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(20),
        },
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: 1.7,
    },

    h3: {
        fontFamily: baseProperties.fontFamily,
        fontSize: pxToRem(28),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(36),
        },
        lineHeight: 1.4,
        ...baseHeadingProperties,
    },

    h4: {
        fontFamily: baseProperties.fontFamily,
        fontSize: pxToRem(24),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(30),
        },
        lineHeight: 1.4,
        ...baseHeadingProperties,
    },

    h5: {
        fontFamily: baseProperties.fontFamily,
        fontSize: pxToRem(20),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(24),
        },
        lineHeight: 1.4,
        ...baseHeadingProperties,
    },

    h6: {
        fontFamily: baseProperties.fontFamily,
        fontSize: pxToRem(18),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(20),
        },
        lineHeight: 1.6,
        ...baseHeadingProperties,
    },

    subtitle2: {
        fontFamily: baseProperties.fontFamily2,
        fontSize: pxToRem(18),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(20),
        },
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: 1.6,
    },

    body2: {
        fontFamily: baseProperties.fontFamily2,
        fontSize: pxToRem(18),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(20),
        },
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: 1.6,
    },

    button: {
        fontFamily: baseProperties.fontFamily,
        fontSize: pxToRem(16),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(18),
        },
        fontWeight: baseProperties.fontWeightLight,
        lineHeight: 1.5,
        textTransform: "uppercase",
    },

    a: {
        fontSize: pxToRem(16),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(18),
        },
    },

    caption: {
        fontFamily: baseProperties.fontFamily,
        fontSize: pxToRem(14),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(16),
        },
        fontWeight: baseProperties.fontWeightLight,
        lineHeight: 1.25,
    },

    overline: {
        fontFamily: baseProperties.fontFamily,
    },

    d1: {
        fontSize: pxToRem(44),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(84),
        },
        ...baseDisplayProperties,
    },

    d2: {
        fontSize: pxToRem(40),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(76),
        },
        ...baseDisplayProperties,
    },

    d3: {
        fontSize: pxToRem(36),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(68),
        },
        ...baseDisplayProperties,
    },

    d4: {
        fontSize: pxToRem(32),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(60),
        },
        ...baseDisplayProperties,
    },

    d5: {
        fontSize: pxToRem(28),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(52),
        },
        ...baseDisplayProperties,
    },

    d6: {
        fontSize: pxToRem(24),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(44),
        },
        ...baseDisplayProperties,
    },

    size: {
        xxs: baseProperties.fontSizeXXS,
        xs: baseProperties.fontSizeXS,
        sm: baseProperties.fontSizeSM,
        md: baseProperties.fontSizeMD,
        lg: baseProperties.fontSizeLG,
        xl: baseProperties.fontSizeXL,
        "2xl": baseProperties.fontSize2XL,
        "3xl": baseProperties.fontSize3XL,
    },

    lineHeight: {
        sm: 1.25,
        md: 1.5,
        lg: 2,
    },
};

export default typography;
